import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, RefreshCw } from "lucide-react";
import React from "react";

import { getReviewsDashboard } from "@/api/reviews";
import { Button } from "@/components/ui/button";
import { extractErrorMessage } from "@/lib/api-errors";
import { cn } from "@/lib/utils";
import type {
  AgentJobName,
  ReviewsDashboardAgentJob,
  ReviewsDashboardDay,
  ReviewsDashboardHiddenPair,
  ReviewsDashboardRow,
} from "@/types/api";

/** Дашборд конвейера отзывов: поток по дням × МП × юрлицо и «светофор» очереди.
 *
 *  Данные — `GET /api/reviews/dashboard` (бэкенд `reviews/dashboard.py`). Дни — московские,
 *  последний в `days` — сегодня (так их строит бэкенд). `received` = null значит «DWH
 *  недоступен», а не ноль: такие ячейки показываем «—», а причину — баннером.
 */

const PLATFORM_LABELS: Record<ReviewsDashboardRow["platform"], string> = {
  wb: "WB",
  ozon: "Ozon",
};

type Metric = keyof Pick<
  ReviewsDashboardDay,
  "received" | "received_text" | "received_rating_only" | "generated" | "approved" | "published"
>;

// sub — подстрока предыдущей метрики (разбивка «Пришло с МП»), рисуется с отступом.
const METRICS: { key: Metric; label: string; sub?: boolean }[] = [
  { key: "received", label: "Пришло с МП" },
  { key: "received_text", label: "с текстом", sub: true },
  { key: "received_rating_only", label: "только оценка", sub: true },
  { key: "generated", label: "Сгенерировано" },
  { key: "approved", label: "Одобрено" },
  { key: "published", label: "Опубликовано" },
];

// Имена заданий Ozon-агента — те же, что в текстах флажков (dashboard.py AGENT_JOB_TITLES).
const AGENT_JOB_TITLES: Record<AgentJobName, string> = {
  poster: "публикатор",
  collector: "сборщик",
  markviewed: "пометка просмотренных",
};

const AGENT_ERROR_MAX = 80;

const WEEKDAYS = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];

const MSK_DATETIME = new Intl.DateTimeFormat("ru-RU", {
  timeZone: "Europe/Moscow",
  day: "2-digit",
  month: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
});

const MSK_TIME = new Intl.DateTimeFormat("ru-RU", {
  timeZone: "Europe/Moscow",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

const MSK_DATE = new Intl.DateTimeFormat("ru-RU", {
  timeZone: "Europe/Moscow",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

function rowName(row: ReviewsDashboardRow): string {
  return `${PLATFORM_LABELS[row.platform]} · ${row.legal_name}`;
}

function rowKey(row: ReviewsDashboardRow): string {
  return `${row.platform}-${row.legal_name}`;
}

/** «2026-09-22» → «пн 22.09». Дата календарная (без зоны) — разбираем как UTC-полночь. */
function formatDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  return `${WEEKDAYS[d.getUTCDay()]} ${dd}.${mm}`;
}

function formatMsk(iso: string): string {
  return `${MSK_DATETIME.format(new Date(iso))} МСК`;
}

function formatMskDate(iso: string): string {
  return MSK_DATE.format(new Date(iso));
}

function formatRelative(iso: string, now: number): string {
  const minutes = Math.floor((now - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return "только что";
  if (minutes < 60) return `${minutes} мин назад`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} ч назад`;
  return `${Math.floor(hours / 24)} дн назад`;
}

/** Итог по метрике за 7 дней; null, если хоть один день неизвестен (DWH недоступен). */
function metricTotal(daily: ReviewsDashboardDay[], metric: Metric): number | null {
  let sum = 0;
  for (const d of daily) {
    const v = d[metric];
    if (v === null) return null;
    sum += v;
  }
  return sum;
}

function CountCell({
  value,
  className,
}: {
  value: number | null;
  className?: string;
}): React.ReactElement {
  return (
    <td
      className={cn(
        "px-2 py-1.5 text-right tabular-nums",
        value === null && "text-muted-foreground",
        value === 0 && "text-muted-foreground/60",
        className,
      )}
      title={value === null ? "DWH недоступен" : undefined}
    >
      {value === null ? "—" : value}
    </td>
  );
}

function LastEvent({
  label,
  iso,
  now,
  emptyText,
}: {
  label: string;
  iso: string | null;
  now: number;
  emptyText: string;
}): React.ReactElement {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      {iso ? (
        <span title={formatMsk(iso)}>{formatRelative(iso, now)}</span>
      ) : (
        <span className="text-muted-foreground">{emptyText}</span>
      )}
    </div>
  );
}

function QueueLine({
  label,
  value,
  alertClass,
  extra,
}: {
  label: string;
  value: number | null; // null — источник недоступен (DWH), показываем «—»
  alertClass?: string;
  extra?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex justify-between gap-2">
      <span className="shrink-0 whitespace-nowrap text-muted-foreground">{label}</span>
      <span className="text-right">
        <span
          className={cn(
            "font-medium tabular-nums",
            value === null && "text-muted-foreground font-normal",
            value === 0 && "text-muted-foreground/60 font-normal",
            value !== null && value > 0 && alertClass,
          )}
          title={value === null ? "DWH недоступен" : undefined}
        >
          {value === null ? "—" : value}
        </span>
        {extra}
      </span>
    </div>
  );
}

function RowCard({
  row,
  dwhDown,
  now,
}: {
  row: ReviewsDashboardRow;
  dwhDown: boolean;
  now: number;
}): React.ReactElement {
  const alert = row.flags.length > 0;
  const q = row.queue;
  return (
    <div
      data-testid={`card-${rowKey(row)}`}
      data-alert={alert ? "true" : "false"}
      className={cn(
        "flex flex-col gap-3 rounded-lg border bg-card p-4 text-sm shadow-sm",
        alert && "border-2 border-red-500",
      )}
    >
      <div className="text-base font-semibold">{rowName(row)}</div>

      {alert && (
        <ul className="flex flex-col gap-1.5">
          {row.flags.map((f, i) => (
            <li
              key={`${f.code}-${i}`}
              className="flex items-start gap-2 rounded-md bg-red-50 px-2 py-1.5 font-medium text-red-700 dark:bg-red-950/40 dark:text-red-400"
            >
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              <span>{f.text}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-col gap-1">
        <QueueLine
          label="Ждут генерации"
          value={row.awaiting_generation}
          extra={
            row.oldest_awaiting_at ? (
              <span className="ml-1 text-xs text-muted-foreground">
                с {formatMsk(row.oldest_awaiting_at)}
              </span>
            ) : null
          }
        />
        <QueueLine label="Ожидают проверки" value={q.pending} />
        <QueueLine
          label="Одобрено, ждут публикации"
          value={q.approved}
          extra={
            q.oldest_approved_at ? (
              <span className="ml-1 text-xs text-muted-foreground">
                с {formatMsk(q.oldest_approved_at)}
              </span>
            ) : null
          }
        />
        <QueueLine label="Ошибки публикации" value={q.failed} alertClass="text-red-600 dark:text-red-400" />
        <QueueLine
          label="Нужен ручной ответ"
          value={q.needs_manual}
          alertClass="text-amber-600 dark:text-amber-400"
          extra={
            q.oldest_needs_manual_at ? (
              <span className="ml-1 text-xs text-muted-foreground">
                с {formatMsk(q.oldest_needs_manual_at)}
              </span>
            ) : null
          }
        />
      </div>

      <div className="flex flex-col gap-1 border-t pt-2">
        <LastEvent
          label="Последний отзыв с МП"
          iso={row.last_received_at}
          now={now}
          emptyText={dwhDown ? "— (DWH недоступен)" : "не было"}
        />
        <LastEvent
          label="Последняя публикация"
          iso={row.last_published_at}
          now={now}
          emptyText="не было"
        />
        {row.platform === "ozon" && (
          <div data-testid="ozon-not-viewed" className="flex justify-between gap-2">
            <span className="text-muted-foreground">Непросмотренные в кабинете</span>
            {row.ozon_not_viewed === null ? (
              <span className="text-muted-foreground" title="Нет успешного отчёта агента">
                —
              </span>
            ) : (
              <span>
                <span className="font-medium tabular-nums">{row.ozon_not_viewed}</span>
                {row.ozon_not_viewed_at && (
                  <span
                    className="ml-1 text-xs text-muted-foreground"
                    title={formatMsk(row.ozon_not_viewed_at)}
                  >
                    {formatRelative(row.ozon_not_viewed_at, now)}
                  </span>
                )}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function RelativeCell({
  iso,
  now,
  emptyText,
}: {
  iso: string | null;
  now: number;
  emptyText: string;
}): React.ReactElement {
  return (
    <td className="whitespace-nowrap px-3 py-1.5">
      {iso ? (
        <span title={formatMsk(iso)}>{formatRelative(iso, now)}</span>
      ) : (
        <span className="text-muted-foreground">{emptyText}</span>
      )}
    </td>
  );
}

/** Блок «Агент Ozon (машина .90)»: по заданию × юрлицу — последний прогон, статус, последний
 *  успешный, ошибка, счётчики. Данные — `agent.jobs` (отчёты `/api/internal/agent-runs`). */
function AgentBlock({
  jobs,
  now,
}: {
  jobs: ReviewsDashboardAgentJob[];
  now: number;
}): React.ReactElement {
  return (
    <div data-testid="agent-block" className="flex flex-col gap-2">
      <h2 className="text-base font-semibold">Агент Ozon (машина .90)</h2>
      <div className="overflow-x-auto rounded-lg border bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40 text-left">
              <th className="px-3 py-2 font-medium">Задание</th>
              <th className="px-3 py-2 font-medium">Юрлицо</th>
              <th className="px-3 py-2 font-medium">Последний прогон</th>
              <th className="px-3 py-2 font-medium">Статус</th>
              <th className="px-3 py-2 font-medium">Последний успешный</th>
              <th className="px-3 py-2 font-medium">Ошибка</th>
              <th className="px-3 py-2 font-medium">Счётчики</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => {
              const key = `${j.job}-${j.legal_name ?? "all"}`;
              const counters = Object.entries(j.counters)
                .map(([k, v]) => `${k} ${v}`)
                .join(" · ");
              const error = j.last_error ?? "";
              return (
                <tr key={key} data-testid={`agent-${key}`} className="border-t">
                  <td className="whitespace-nowrap px-3 py-1.5">{AGENT_JOB_TITLES[j.job]}</td>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    {j.legal_name ?? (
                      <span className="text-muted-foreground">весь прогон</span>
                    )}
                  </td>
                  <RelativeCell iso={j.last_run_at} now={now} emptyText="не отчитывался" />
                  <td
                    className={cn(
                      "px-3 py-1.5 font-medium",
                      j.last_status === "ok" && "text-green-700 dark:text-green-400",
                      (j.last_status === "error" || j.last_status === "timeout") &&
                        "text-red-600 dark:text-red-400",
                      j.last_status === null && "font-normal text-muted-foreground",
                    )}
                  >
                    {j.last_status ?? "—"}
                  </td>
                  <RelativeCell iso={j.last_success_at} now={now} emptyText="не было" />
                  <td className="max-w-xs px-3 py-1.5 text-red-700 dark:text-red-400" title={error || undefined}>
                    {error.length > AGENT_ERROR_MAX
                      ? `${error.slice(0, AGENT_ERROR_MAX)}…`
                      : error}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-muted-foreground tabular-nums">
                    {counters}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FlowTable({
  days,
  rows,
}: {
  days: string[];
  rows: ReviewsDashboardRow[];
}): React.ReactElement {
  const today = days[days.length - 1];
  const colCount = days.length + 2;
  return (
    <div className="overflow-x-auto rounded-lg border bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/40">
            <th className="px-3 py-2 text-left font-medium">Метрика</th>
            {days.map((d) => (
              <th
                key={d}
                className={cn(
                  "whitespace-nowrap px-2 py-2 text-right font-medium",
                  d === today && "bg-blue-50 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300",
                )}
              >
                {formatDay(d)}
                {d === today && <span className="block text-xs font-normal">сегодня</span>}
              </th>
            ))}
            <th className="px-3 py-2 text-right font-medium">Итого</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <React.Fragment key={rowKey(row)}>
              <tr className="border-t bg-muted/20">
                <td colSpan={colCount} className="px-3 py-1.5 font-semibold">
                  {rowName(row)}
                </td>
              </tr>
              {METRICS.map((m) => (
                <tr key={m.key} data-testid={`metric-${rowKey(row)}-${m.key}`}>
                  <td
                    className={cn(
                      "whitespace-nowrap px-3 py-1.5 text-muted-foreground",
                      m.sub ? "pl-10 text-xs" : "pl-6",
                    )}
                  >
                    {m.label}
                  </td>
                  {row.daily.map((d) => (
                    <CountCell
                      key={d.date}
                      value={d[m.key]}
                      className={d.date === today ? "bg-blue-50/60 dark:bg-blue-950/30" : undefined}
                    />
                  ))}
                  <CountCell value={metricTotal(row.daily, m.key)} className="font-semibold" />
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HiddenInactiveNote({
  hidden,
}: {
  hidden: ReviewsDashboardHiddenPair[];
}): React.ReactElement {
  return (
    <div data-testid="hidden-inactive" className="text-sm text-muted-foreground">
      Скрыты неактивные больше 30 дней:{" "}
      {hidden.map((h, i) => (
        <span key={`${h.platform}-${h.legal_name}`}>
          {i > 0 && ", "}
          {PLATFORM_LABELS[h.platform]} · {h.legal_name}{" "}
          {h.last_activity_at
            ? `(последняя активность ${formatMskDate(h.last_activity_at)})`
            : "(активности не было)"}
        </span>
      ))}
    </div>
  );
}

export function ReviewsDashboardPage(): React.ReactElement {
  const query = useQuery({
    queryKey: ["reviews-dashboard"],
    queryFn: getReviewsDashboard,
  });
  const data = query.data;
  const now = Date.now();
  const ozonAgentJobs = data ? data.agent.jobs.filter((j) => j.platform === "ozon") : [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold">Дашборд отзывов</h1>
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          {data && (
            <span title={formatMsk(data.generated_at)}>
              Данные на {MSK_TIME.format(new Date(data.generated_at))} МСК
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            <RefreshCw className={cn("size-4", query.isFetching && "animate-spin")} />
            Обновить
          </Button>
        </div>
      </div>

      {query.isError && (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          Не удалось получить дашборд: {extractErrorMessage(query.error, "ошибка запроса")}
          {data && " — ниже показаны данные прошлой загрузки."}
        </div>
      )}

      {query.isPending && <div className="text-sm text-muted-foreground">Загрузка…</div>}

      {data && data.rows.length === 0 && data.hidden_inactive.length === 0 && (
        <div className="rounded-md border px-3 py-2 text-sm text-muted-foreground">
          Нет ни одной пары МП × юрлицо: ни в портале, ни в DWH нет отзывов.
        </div>
      )}

      {data && data.rows.length > 0 && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {data.rows.map((row) => (
              <RowCard key={rowKey(row)} row={row} dwhDown={data.dwh_error !== null} now={now} />
            ))}
          </div>
        </>
      )}

      {data && ozonAgentJobs.length > 0 && <AgentBlock jobs={ozonAgentJobs} now={now} />}

      {data && data.dwh_error !== null && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm font-medium text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <span>Не удалось получить отзывы из DWH: {data.dwh_error}</span>
        </div>
      )}

      {data && data.rows.length > 0 && (
        <>
          <FlowTable days={data.days} rows={data.rows} />
        </>
      )}

      {data && data.hidden_inactive.length > 0 && (
        <HiddenInactiveNote hidden={data.hidden_inactive} />
      )}
    </div>
  );
}
